import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { StatusBadge, EmptyState } from "@/components/app/ui-bits";
import {
  Plus, Receipt, ChartBar, Coins, FileText, DownloadSimple,
  Scales, ArrowRight, Trash, PencilSimple, Check, CurrencyDollar,
  Sparkle, EnvelopeSimple, ArrowsClockwise,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import AiExtractDialog from "@/components/app/AiExtractDialog";
import FinanceSmartInsights from "@/components/app/FinanceSmartInsights";

// ══════════════════════════════════════════════════════════════
// Section 12 — Audit Figures (YOY) + Baseline Reconcile + AI Extract
// ══════════════════════════════════════════════════════════════
export function AuditFiguresTab({ canEdit }) {
  const [data, setData] = useState(null);
  const [reconcile, setReconcile] = useState(null);
  const [reconcileYear, setReconcileYear] = useState(new Date().getFullYear() - 1);
  const [baselines, setBaselines] = useState([]);
  const [loadingRec, setLoadingRec] = useState(false);

  const loadAll = async () => {
    try {
      const [{ data: d }, { data: bl }] = await Promise.all([
        api.get("/finance/audit-figures"),
        api.get("/finance/audit-baselines"),
      ]);
      setData(d);
      setBaselines(bl.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadReconcile = async (yr) => {
    setLoadingRec(true);
    try {
      const { data: r } = await api.get("/finance/audit-reconcile", { params: { fiscal_year: yr } });
      setReconcile(r);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingRec(false); }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadReconcile(reconcileYear); /* eslint-disable-next-line */ }, [reconcileYear]);

  if (!data) return <div className="font-mono-alt text-xs uppercase tracking-[0.25em] text-zinc-500">Loading…</div>;

  const is = data.income_statement;
  const bs = data.balance_sheet;

  const yoy = (cur, prev) => {
    if (!prev) return null;
    const pct = ((cur - prev) / Math.abs(prev || 1)) * 100;
    return { pct: pct.toFixed(1), positive: pct >= 0 };
  };

  const onAuditSaved = async () => {
    toast.success("Audit baseline saved · refreshing figures");
    await loadAll();
    await loadReconcile(reconcileYear);
  };

  return (
    <div className="space-y-5">
      {/* Action strip */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-slate-500">
          {baselines.length > 0
            ? <>Audit baselines on file: <span className="font-mono-alt font-semibold">{baselines.map((b) => b.fiscal_year).join(" · ")}</span></>
            : <>No audit baselines uploaded yet — using built-in 2025 audit-confirmed seed.</>}
        </div>
        {canEdit && (
          <AiExtractDialog
            module="audit_report"
            onSaved={onAuditSaved}
            saveLabel="Save audit baseline"
            trigger={
              <Button size="sm" className="btn-primary-blue" data-testid="audit-ai-extract-btn">
                <Sparkle size={12} className="mr-1" /> Upload audit report (AI Extract)
              </Button>
            }
          />
        )}
      </div>

      <div className="dc-card overflow-hidden">
        <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-2">
          <ChartBar size={18} weight="fill" style={{ color: "#1E5CBA" }} />
          <h3 className="font-display text-[15px] font-semibold text-slate-900">Income Statement · Year-on-Year</h3>
        </div>
        <div className="responsive-table-wrap">
          <table className="data-table w-full min-w-[700px]" data-testid="audit-income-statement">
            <thead>
              <tr>
                <th>Line</th>
                {is.map((r) => <th key={r.year} className="text-right">{r.year}</th>)}
                <th className="text-right">Δ YoY</th>
              </tr>
            </thead>
            <tbody>
              {[
                { k: "revenue",      l: "Revenue",       cls: "text-emerald-700" },
                { k: "project_cost", l: "Project costs", cls: "text-red-700" },
                { k: "overhead",     l: "Overhead",      cls: "text-red-700" },
              ].map((row) => {
                const cur = is.at(-1)?.[row.k];
                const prev = is.length > 1 ? is.at(-2)?.[row.k] : null;
                const delta = yoy(cur, prev);
                return (
                  <tr key={row.k} data-testid={`is-row-${row.k}`}>
                    <td className="px-5 py-3 font-semibold text-slate-900">{row.l}</td>
                    {is.map((r) => <td key={r.year} className={`px-5 py-3 text-right font-mono-alt tabular-nums ${row.cls}`}>{formatCurrency(r[row.k])}</td>)}
                    <td className={`px-5 py-3 text-right font-mono-alt tabular-nums text-xs ${delta?.positive ? "text-emerald-700" : "text-red-700"}`}>{delta ? `${delta.positive ? "+" : ""}${delta.pct}%` : "—"}</td>
                  </tr>
                );
              })}
              <tr className="border-t-2 border-slate-200">
                <td className="px-5 py-3 font-display font-bold text-slate-900">Net</td>
                {is.map((r) => <td key={r.year} className={`px-5 py-3 text-right font-mono-alt font-bold tabular-nums ${r.net >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatCurrency(r.net)}</td>)}
                <td className="px-5 py-3 text-right text-xs text-slate-500">—</td>
              </tr>
              <tr>
                <td className="px-5 py-3 text-sm text-slate-500">Net margin</td>
                {is.map((r) => <td key={r.year} className="px-5 py-3 text-right font-mono-alt tabular-nums text-xs text-slate-600">{r.margin_pct}%</td>)}
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <BalanceCard title="Assets" tone="emerald" rows={[
          ["Cash & equivalents", bs.assets.cash_and_equivalents],
          ["Accounts receivable", bs.assets.accounts_receivable],
          ["Fixed assets (NBV)", bs.assets.fixed_assets_nbv],
        ]} total={bs.assets.total} testid="bs-assets" />
        <BalanceCard title="Liabilities" tone="red" rows={[
          ["Accounts payable", bs.liabilities.accounts_payable],
        ]} total={bs.liabilities.total} testid="bs-liabilities" />
        <BalanceCard title="Equity" tone="blue" rows={[
          ["Retained earnings", bs.equity.retained_earnings],
        ]} total={bs.equity.total} testid="bs-equity" />
      </div>
      <div className="text-right text-xs text-slate-500" data-testid="bs-as-of">Balance sheet as of {formatDate(bs.as_of)}</div>

      {/* Reconcile vs Audit Baseline */}
      <div className="dc-card overflow-hidden" data-testid="audit-reconcile-card">
        <div className="border-b border-slate-100 px-5 py-3 flex flex-wrap items-center gap-3">
          <Scales size={18} weight="fill" className="text-amber-600" />
          <h3 className="font-display text-[15px] font-semibold text-slate-900">Reconcile vs Audit Baseline</h3>
          <div className="ml-auto flex items-center gap-2">
            <Label className="text-xs text-slate-500">Fiscal year</Label>
            <Select value={String(reconcileYear)} onValueChange={(v) => setReconcileYear(parseInt(v, 10))}>
              <SelectTrigger className="h-8 w-28" data-testid="reconcile-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[...new Set([...(baselines || []).map((b) => b.fiscal_year), 2025, new Date().getFullYear() - 1])]
                  .sort((a, b) => b - a)
                  .map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={() => loadReconcile(reconcileYear)} data-testid="reconcile-refresh">
              <ArrowsClockwise size={12} className={loadingRec ? "animate-spin" : ""} />
            </Button>
          </div>
        </div>

        {!reconcile || !reconcile.lines?.length ? (
          <div className="px-5 py-6 text-sm text-slate-500" data-testid="reconcile-empty">
            {reconcile?.message || "Loading…"}
          </div>
        ) : (
          <>
            {reconcile.baseline?.is_default_seed && (
              <div className="border-b border-amber-100 bg-amber-50/70 px-5 py-2 text-xs text-amber-800">
                Showing built-in 2025 baseline. Upload the signed audit report to replace it.
              </div>
            )}
            <div className="responsive-table-wrap">
              <table className="data-table w-full min-w-[800px]" data-testid="reconcile-table">
                <thead>
                  <tr>
                    <th>Line</th>
                    <th>Bucket</th>
                    <th className="text-right">Audit baseline</th>
                    <th className="text-right">Live · current</th>
                    <th className="text-right">Δ Delta</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reconcile.lines.map((row) => {
                    const tone = {
                      matched: "text-emerald-700",
                      minor_drift: "text-amber-700",
                      drift: "text-red-700",
                      no_baseline: "text-slate-500",
                    }[row.status] || "text-slate-700";
                    const badge = {
                      matched: "active",
                      minor_drift: "expiring_soon",
                      drift: "expired",
                      no_baseline: "pending_review",
                    }[row.status] || "pending_review";
                    return (
                      <tr key={row.key} data-testid={`reconcile-row-${row.key}`}>
                        <td className="px-5 py-2.5 font-semibold text-slate-900">{row.label}</td>
                        <td className="px-5 py-2.5 text-xs text-slate-500">{row.bucket}</td>
                        <td className="px-5 py-2.5 text-right font-mono-alt tabular-nums">
                          {row.baseline == null ? "—" : formatCurrency(row.baseline)}
                        </td>
                        <td className="px-5 py-2.5 text-right font-mono-alt tabular-nums">
                          {formatCurrency(row.live || 0)}
                        </td>
                        <td className={`px-5 py-2.5 text-right font-mono-alt tabular-nums ${tone}`}>
                          {row.delta == null ? "—" : (row.delta >= 0 ? "+" : "") + formatCurrency(row.delta)}
                        </td>
                        <td className="px-5 py-2.5"><StatusBadge status={badge} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="border-t border-slate-100 bg-slate-50 px-5 py-2 text-[11px] text-slate-500">
              Drift thresholds: matched ≤1% · minor 1-5% · drift &gt;5%. Live figures are computed from finance_entries / assets / AR / AP / bank reconciliations.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function BalanceCard({ title, tone, rows, total, testid }) {
  const toneCls = { emerald: "text-emerald-700", red: "text-red-700", blue: "text-blue-700" }[tone] || "text-slate-700";
  return (
    <div className="dc-card overflow-hidden" data-testid={testid}>
      <div className="border-b border-slate-100 px-5 py-3 overline">{title}</div>
      <div className="p-5">
        <dl className="space-y-2 text-sm">
          {rows.map(([l, v]) => (
            <div key={l} className="flex justify-between"><dt className="text-slate-500">{l}</dt><dd className="font-mono-alt tabular-nums">{formatCurrency(v)}</dd></div>
          ))}
        </dl>
        <div className="mt-4 flex items-baseline justify-between border-t border-slate-100 pt-3">
          <dt className="font-semibold text-slate-900">Total {title.toLowerCase()}</dt>
          <dd className={`font-display text-xl font-bold tabular-nums ${toneCls}`}>{formatCurrency(total)}</dd>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Section 13.1 — Invoice Builder
// ══════════════════════════════════════════════════════════════
export function InvoiceBuilderTab({ canEdit, projects }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/finance/invoices");
    setItems(data.items || []);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Download PDF via axios so the JWT goes through, then open the resulting blob.
  const downloadPdf = async (inv) => {
    try {
      const res = await api.get(`/finance/invoices/${inv.id}/export.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.invoice_no}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // "Send invoice" — downloads PDF locally and opens user's default mail client
  // pre-filled with subject/body. The user attaches the just-downloaded PDF.
  const [emailing, setEmailing] = useState(null); // invoice being emailed
  const [emailForm, setEmailForm] = useState({ to: "", cc: "", subject: "", body: "" });

  const openEmail = (inv) => {
    setEmailing(inv);
    setEmailForm({
      to: "",
      cc: "finance@descon.group",
      subject: `Invoice ${inv.invoice_no} from Descon Group · ${formatCurrency(inv.total)}`,
      body:
        `Dear ${inv.client_name},\n\n` +
        `Please find attached invoice ${inv.invoice_no} for ${formatCurrency(inv.total)} (USD), ` +
        `issued ${formatDate(inv.issue_date)} and due ${formatDate(inv.due_date)}.\n\n` +
        `Payment terms: Net 30. Wire to Premier Bank · Account 020300078001.\n\n` +
        `Kindly confirm receipt and let us know if you need any clarification.\n\n` +
        `Warm regards,\nDescon Group Finance\nfinance@descon.group`,
    });
  };

  const sendEmail = async () => {
    if (!emailing) return;
    if (!emailForm.to.trim()) return toast.error("Recipient email is required");
    try {
      // Step 1: download the PDF locally so the user can attach it
      const res = await api.get(`/finance/invoices/${emailing.id}/export.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${emailing.invoice_no}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);

      // Step 2: open the user's mail client with the prepared message
      const params = new URLSearchParams({
        subject: emailForm.subject,
        body: emailForm.body + `\n\n[Attach the just-downloaded ${emailing.invoice_no}.pdf]`,
      });
      if (emailForm.cc) params.set("cc", emailForm.cc);
      const mailto = `mailto:${encodeURIComponent(emailForm.to)}?${params.toString()}`;
      window.location.href = mailto;

      // Step 3: stamp the invoice as "sent" so finance has an audit trail
      try {
        await api.post(`/finance/invoices/${emailing.id}/mark-sent`, {
          recipient: emailForm.to, cc: emailForm.cc,
        });
      } catch { /* non-fatal */ }

      toast.success("Invoice PDF downloaded · email composer opened");
      setEmailing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const mergeToAr = async () => {
    try {
      const { data } = await api.post("/finance/invoices/merge-to-ar");
      toast.success(data.merged ? `${data.merged} invoice(s) merged into AR (+GL journals)` : "All sent invoices are already linked to AR");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="dc-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="font-display text-[15px] font-semibold text-slate-900">Invoices
            <div className="text-[11px] font-normal text-slate-400 font-body">Sent invoices flow into AR &amp; the GL (Dr 1130 · Cr 4110) via “Merge sent → AR”.</div>
          </h3>
          <div className="flex gap-2">
            {canEdit && <Button size="sm" variant="outline" onClick={mergeToAr} data-testid="invoice-merge-ar-btn">Merge sent → AR</Button>}
            {canEdit && <Button size="sm" onClick={() => setOpen(true)} data-testid="invoice-new-btn"><Plus size={12} className="mr-1" /> New invoice</Button>}
          </div>
        </div>
        {items.length === 0 ? <EmptyState icon={Receipt} title="No invoices yet" description="Create your first invoice to issue & download a PDF." />
         : (
          <div className="responsive-table-wrap">
            <table className="data-table w-full min-w-[900px]">
              <thead><tr><th>Invoice #</th><th>Client</th><th>Issued</th><th>Due</th><th className="text-right">Amount</th><th>Status</th><th className="text-right">Action</th></tr></thead>
              <tbody>
                {items.map((inv) => (
                  <tr key={inv.id} data-testid={`invoice-row-${inv.invoice_no}`}>
                    <td className="px-5 py-3 font-mono-alt text-xs">{inv.invoice_no}</td>
                    <td className="px-5 py-3 font-semibold text-slate-900">{inv.client_name}</td>
                    <td className="px-5 py-3 text-xs">{formatDate(inv.issue_date)}</td>
                    <td className="px-5 py-3 text-xs">{formatDate(inv.due_date)}</td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums font-semibold">{formatCurrency(inv.total)}</td>
                    <td className="px-5 py-3">
                      <div className="flex flex-col items-start gap-1">
                        <StatusBadge status={inv.status === "paid" ? "approved" : inv.status === "sent" ? "active" : "pending"} />
                        {inv.sent_at && <span className="font-mono-alt text-[10px] text-slate-500">sent {formatDate(inv.sent_at)}</span>}
                        {inv.ar_invoice_id && <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 font-mono-alt text-[9px] font-semibold uppercase tracking-wider text-emerald-700" data-testid={`invoice-ar-linked-${inv.invoice_no}`}>AR linked ✓</span>}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => downloadPdf(inv)} data-testid={`invoice-pdf-${inv.invoice_no}`}><DownloadSimple size={12} className="mr-1" /> PDF</Button>
                        {canEdit && <Button size="sm" variant="outline" onClick={() => openEmail(inv)} data-testid={`invoice-email-${inv.invoice_no}`}><EnvelopeSimple size={12} className="mr-1" /> Email</Button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <InvoiceDialog open={open} onClose={() => setOpen(false)} onSaved={load} projects={projects} />

      <Dialog open={!!emailing} onOpenChange={(o) => !o && setEmailing(null)}>
        <DialogContent className="sm:max-w-xl" data-testid="invoice-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <EnvelopeSimple size={18} weight="fill" className="text-[#1E5CBA]" />
              Send invoice {emailing?.invoice_no}
            </DialogTitle>
            <DialogDescription>
              The PDF will download to your computer and your default mail client will open with this message pre-filled. Attach the downloaded PDF before sending.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label>To *</Label>
                <Input
                  type="email"
                  placeholder="client.finance@example.com"
                  value={emailForm.to}
                  onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                  data-testid="invoice-email-to"
                />
              </div>
              <div className="md:col-span-2">
                <Label>CC</Label>
                <Input
                  value={emailForm.cc}
                  onChange={(e) => setEmailForm({ ...emailForm, cc: e.target.value })}
                  data-testid="invoice-email-cc"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Subject</Label>
                <Input
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  data-testid="invoice-email-subject"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Message</Label>
                <Textarea
                  rows={8}
                  value={emailForm.body}
                  onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                  data-testid="invoice-email-body"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailing(null)}>Cancel</Button>
            <Button onClick={sendEmail} className="btn-primary-blue" data-testid="invoice-email-send">
              <EnvelopeSimple size={14} className="mr-1" /> Download PDF & open mail
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InvoiceDialog({ open, onClose, onSaved, projects }) {
  const today = new Date().toISOString().slice(0, 10);
  const due = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  const newLine = () => ({ _lid: `l-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, description: "", quantity: 1, unit_price: 0 });
  const [form, setForm] = useState({ client_id: null, client_name: "", client_address: "", issue_date: today, due_date: due, project_id: null, tax_rate_pct: 5, discount: 0, notes: "" });
  const [lines, setLines] = useState([newLine()]);
  const [clients, setClients] = useState([]);
  const [billing, setBilling] = useState(null);
  const [pct, setPct] = useState("");
  useEffect(() => { if (!open) {
    setForm({ client_id: null, client_name: "", client_address: "", issue_date: today, due_date: due, project_id: null, tax_rate_pct: 5, discount: 0, notes: "" });
    setLines([newLine()]);
    setPct("");
  } else {
    api.get("/clients").then((r) => setClients((r.data.items || []).filter((c) => !["rejected", "archived"].includes(c.status))))
      .catch(() => setClients([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }}, [open]);

  const pickClient = (v) => {
    if (v === "manual") { setForm({ ...form, client_id: null }); return; }
    const c = clients.find((x) => x.id === v);
    if (!c) return;
    setForm({
      ...form, client_id: c.id, client_name: c.name,
      client_address: c.billing_address || c.address || "",
      project_id: null,
    });
  };
  const norm = (s) => (s || "").trim().toLowerCase();
  const clientProjects = form.client_id
    ? (projects || []).filter((p) => p.client_id === form.client_id || norm(p.client) === norm(form.client_name))
    : [];
  const otherProjects = (projects || []).filter((p) => !clientProjects.includes(p));

  // Contract/billing snapshot for the selected project — powers % billing
  useEffect(() => {
    if (!open || !form.project_id) { setBilling(null); return; }
    api.get(`/finance/invoices/project-billing/${form.project_id}`)
      .then((r) => setBilling(r.data)).catch(() => setBilling(null));
  }, [open, form.project_id]);

  const addPctLine = () => {
    const p = parseFloat(pct);
    if (!p || p <= 0) return toast.error("Enter a percentage first");
    if (!billing || !(billing.contract_value > 0)) return toast.error("This project has no contract value on file — set it on the project first");
    const amount = Math.round(billing.contract_value * p) / 100;
    const nonEmpty = lines.filter((l) => (l.description || "").trim() || (l.unit_price || 0) > 0);
    setLines([...nonEmpty, {
      _lid: `l-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      description: `${p}% of contract value — ${billing.project_name}${billing.project_code ? ` (${billing.project_code})` : ""}`,
      quantity: 1, unit_price: amount,
    }]);
    setPct("");
  };

  const subtotal = lines.reduce((a, l) => a + (l.quantity || 0) * (l.unit_price || 0), 0);
  const taxable = subtotal - (form.discount || 0);
  const tax = taxable * (form.tax_rate_pct || 0) / 100;
  const total = taxable + tax;

  const submit = async () => {
    if (!form.client_name.trim()) return toast.error("Client name is required");
    if (lines.length === 0 || lines.every((l) => !l.description)) return toast.error("Add at least one line item");
    try {
      await api.post("/finance/invoices", { ...form, lines });
      toast.success("Invoice created");
      onClose(); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New invoice</DialogTitle><DialogDescription>USD only · Auto-numbered DSC-YYYY-NNN</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="md:col-span-2"><Label>Client (from register)</Label>
              <Select value={form.client_id || "manual"} onValueChange={pickClient}>
                <SelectTrigger data-testid="inv-client-select"><SelectValue placeholder="Pick a registered client" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="manual">— Enter manually (not in register) —</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} {c.client_code ? `· ${c.client_code}` : ""}{c.status !== "active" ? ` (${c.status.replace(/_/g, " ")})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.client_id && <p className="mt-1 text-[10px] text-emerald-700">Billing details auto-filled from the client register — projects below are filtered to this client.</p>}
            </div>
            <div><Label>Client name *</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="inv-client" disabled={!!form.client_id} /></div>
            <div><Label>Client address</Label><Input value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} /></div>
            <div><Label>Issue date</Label><Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></div>
            <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            <div><Label>Project (optional)</Label>
              <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? null : v })}>
                <SelectTrigger data-testid="inv-project-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none">— None —</SelectItem>
                  {form.client_id ? (
                    <>
                      {clientProjects.length > 0 ? (
                        <SelectGroup>
                          <SelectLabel>{form.client_name}'s projects</SelectLabel>
                          {clientProjects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectGroup>
                      ) : (
                        <div className="px-2 py-1.5 text-[10px] text-amber-700">No projects linked to this client yet</div>
                      )}
                      {otherProjects.length > 0 && (
                        <SelectGroup>
                          <SelectLabel>Other projects (different client)</SelectLabel>
                          {otherProjects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.client ? ` · ${p.client}` : ""}</SelectItem>)}
                        </SelectGroup>
                      )}
                    </>
                  ) : (
                    (projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.client ? ` · ${p.client}` : ""}</SelectItem>)
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Tax %</Label><Input type="number" step="0.5" value={form.tax_rate_pct} onChange={(e) => setForm({ ...form, tax_rate_pct: parseFloat(e.target.value || 0) })} /></div>
              <div><Label>Discount</Label><Input type="number" step="0.01" value={form.discount} onChange={(e) => setForm({ ...form, discount: parseFloat(e.target.value || 0) })} /></div>
            </div>
            {billing && (
              <div className="md:col-span-2 rounded-md border border-blue-100 bg-blue-50/50 p-3" data-testid="inv-project-snapshot">
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-slate-700">
                  <span className="font-mono-alt text-[10px] font-semibold uppercase tracking-widest text-[#1E5CBA]">
                    Contract snapshot{billing.project_code ? ` · ${billing.project_code}` : ""}
                  </span>
                  <span>Contract value <b className="font-mono-alt tabular-nums">{billing.contract_value > 0 ? formatCurrency(billing.contract_value) : "—"}</b></span>
                  <span>Work done <b>{billing.progress_pct}%</b></span>
                  <span>Billed so far <b className="font-mono-alt tabular-nums">{formatCurrency(billing.billed_to_date)}</b>
                    {billing.pct_billed != null && <span className="text-slate-500"> ({billing.pct_billed}%)</span>}
                  </span>
                  {billing.remaining_to_bill != null && (
                    <span>Remaining <b className="font-mono-alt tabular-nums">{formatCurrency(billing.remaining_to_bill)}</b></span>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Input type="number" step="0.1" min="0" placeholder="e.g. 30" className="h-8 w-24"
                         value={pct} onChange={(e) => setPct(e.target.value)} data-testid="inv-pct-input" />
                  <span className="text-xs text-slate-600">% of contract value</span>
                  <Button type="button" size="sm" variant="outline" className="h-8" onClick={addPctLine} data-testid="inv-pct-add">
                    Add as line item{pct && billing.contract_value > 0 ? ` (${formatCurrency(Math.round(billing.contract_value * (parseFloat(pct) || 0)) / 100)})` : ""}
                  </Button>
                </div>
                <div className="mt-1 text-[10px] text-slate-500">Project details (contract no., % complete, billed to date) are printed on the invoice PDF automatically.</div>
              </div>
            )}
          </div>

          <div className="dc-card overflow-hidden">
            <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
              <span className="overline">Line items</span>
              <Button size="sm" variant="outline" onClick={() => setLines([...lines, newLine()])} data-testid="inv-add-line"><Plus size={12} className="mr-1" /> Add line</Button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50"><tr><th className="px-3 py-2 text-left">Description</th><th className="px-3 py-2 text-right w-20">Qty</th><th className="px-3 py-2 text-right w-28">Unit $</th><th className="px-3 py-2 text-right w-28">Amount</th><th className="w-10"></th></tr></thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={l._lid} data-testid={`inv-line-${i}`}>
                    <td className="px-3 py-1.5"><Input value={l.description} onChange={(e) => setLines(lines.map((x, j) => i === j ? { ...x, description: e.target.value } : x))} /></td>
                    <td className="px-3 py-1.5"><Input type="number" step="0.5" value={l.quantity} onChange={(e) => setLines(lines.map((x, j) => i === j ? { ...x, quantity: parseFloat(e.target.value || 0) } : x))} className="text-right" /></td>
                    <td className="px-3 py-1.5"><Input type="number" step="0.01" value={l.unit_price} onChange={(e) => setLines(lines.map((x, j) => i === j ? { ...x, unit_price: parseFloat(e.target.value || 0) } : x))} className="text-right" /></td>
                    <td className="px-3 py-1.5 text-right font-mono-alt tabular-nums">{formatCurrency((l.quantity || 0) * (l.unit_price || 0))}</td>
                    <td className="px-1"><Button size="sm" variant="ghost" onClick={() => setLines(lines.filter((_, j) => j !== i))} disabled={lines.length === 1}><Trash size={12} /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="border-t border-slate-100 bg-slate-50 px-3 py-3 text-sm space-y-1">
              <div className="flex justify-end gap-8"><span className="text-slate-500">Subtotal</span><span className="font-mono-alt tabular-nums w-28 text-right">{formatCurrency(subtotal)}</span></div>
              <div className="flex justify-end gap-8"><span className="text-slate-500">Tax</span><span className="font-mono-alt tabular-nums w-28 text-right">{formatCurrency(tax)}</span></div>
              <div className="flex justify-end gap-8 border-t border-slate-200 pt-1"><span className="font-bold text-slate-900">Total</span><span className="font-display font-bold tabular-nums w-28 text-right">{formatCurrency(total)}</span></div>
            </div>
          </div>

          <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit} className="btn-primary-blue" data-testid="inv-save-btn">Create invoice</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════
// Section 13.2 — Budget Tracker
// ══════════════════════════════════════════════════════════════
export function BudgetTrackerTab({ canEdit }) {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ fiscal_year: new Date().getFullYear(), category: "Project", subcategory: "", budgeted_amount: 0, notes: "" });

  const load = async () => {
    const { data } = await api.get("/finance/budgets");
    setData(data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try { await api.post("/finance/budgets", form); toast.success("Budget saved"); setAdding(false); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <div className="font-mono-alt text-xs uppercase tracking-[0.25em] text-zinc-500">Loading…</div>;

  const t = data.totals;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <div className="dc-card p-4 border-l-4 border-l-[#1E5CBA]"><div className="overline">FY {data.fiscal_year} Budget</div><div className="mt-1 font-display text-2xl font-bold tabular-nums">{formatCurrency(t.budgeted)}</div></div>
        <div className="dc-card p-4 border-l-4 border-l-amber-500"><div className="overline">Actual YTD</div><div className="mt-1 font-display text-2xl font-bold tabular-nums text-amber-700">{formatCurrency(t.actual)}</div></div>
        <div className={`dc-card p-4 border-l-4 ${t.variance >= 0 ? "border-l-emerald-500" : "border-l-red-500"}`}><div className="overline">Variance</div><div className={`mt-1 font-display text-2xl font-bold tabular-nums ${t.variance >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatCurrency(t.variance)}</div></div>
        <div className="dc-card p-4 border-l-4 border-l-slate-400"><div className="overline">Used</div><div className="mt-1 font-display text-2xl font-bold tabular-nums">{t.pct_used}%</div></div>
      </div>

      <div className="dc-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="font-display text-[15px] font-semibold text-slate-900">Budget vs Actual · FY{data.fiscal_year}</h3>
          {canEdit && <Button size="sm" onClick={() => setAdding(true)} data-testid="budget-add-btn"><Plus size={12} className="mr-1" /> Add line</Button>}
        </div>
        <div className="responsive-table-wrap">
          <table className="data-table w-full min-w-[900px]">
            <thead><tr><th>Category</th><th>Subcategory</th><th className="text-right">Budgeted</th><th className="text-right">Actual</th><th className="text-right">Variance</th><th className="text-right">Used</th><th>Status</th></tr></thead>
            <tbody>
              {data.items.map((b) => (
                <tr key={b.id} data-testid={`budget-row-${b.subcategory || b.category}`}>
                  <td className="px-5 py-3 font-semibold text-slate-900">{b.category}</td>
                  <td className="px-5 py-3 text-xs text-slate-600">{b.subcategory || "—"}</td>
                  <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{formatCurrency(b.budgeted_amount)}</td>
                  <td className="px-5 py-3 text-right font-mono-alt tabular-nums text-amber-700">{formatCurrency(b.actual)}</td>
                  <td className={`px-5 py-3 text-right font-mono-alt tabular-nums ${b.variance >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatCurrency(b.variance)}</td>
                  <td className="px-5 py-3 text-right font-mono-alt tabular-nums text-xs">
                    <div className="inline-flex items-center gap-2">
                      <div className="w-20 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                        <div className={`h-full ${b.alert_status === "over" ? "bg-red-500" : b.alert_status === "warning" ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, b.pct_used)}%` }} />
                      </div>
                      <span>{b.pct_used}%</span>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    {b.alert_status === "over" ? <StatusBadge status="rejected" /> : b.alert_status === "warning" ? <StatusBadge status="expiring_soon" /> : <StatusBadge status="active" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={adding} onOpenChange={(o) => !o && setAdding(false)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add budget line</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Fiscal year</Label><Input type="number" value={form.fiscal_year} onChange={(e) => setForm({ ...form, fiscal_year: parseInt(e.target.value || 0) })} /></div>
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Project">Project</SelectItem>
                    <SelectItem value="Overhead">Overhead</SelectItem>
                    <SelectItem value="Payroll">Payroll</SelectItem>
                    <SelectItem value="Capital">Capital expenditure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Subcategory</Label><Input value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} placeholder="e.g. Civil Works" /></div>
            <div><Label>Budgeted amount</Label><Input type="number" step="0.01" value={form.budgeted_amount} onChange={(e) => setForm({ ...form, budgeted_amount: parseFloat(e.target.value || 0) })} data-testid="budget-amt" /></div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={submit} className="btn-primary-blue" data-testid="budget-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Section 13.3 — Tax Liability
// ══════════════════════════════════════════════════════════════
export function TaxTab({ canEdit }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ period: "", tax_type: "corporate_income", base_amount: 0, rate_pct: 5, notes: "" });

  const load = async () => { const { data } = await api.get("/finance/tax-liabilities"); setItems(data.items || []); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try { await api.post("/finance/tax-liabilities", form); toast.success("Tax record saved"); setOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const markPaid = async (id) => {
    try { await api.post(`/finance/tax-liabilities/${id}/mark-paid`); toast.success("Marked as paid"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const totalDue = items.filter((i) => i.status !== "paid").reduce((a, i) => a + (i.liability || 0), 0);
  const overdue = items.filter((i) => i.status === "overdue").reduce((a, i) => a + (i.liability || 0), 0);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="dc-card p-4 border-l-4 border-l-[#1E5CBA]"><div className="overline">Total liability</div><div className="mt-1 font-display text-2xl font-bold tabular-nums">{formatCurrency(items.reduce((a, i) => a + (i.liability || 0), 0))}</div></div>
        <div className="dc-card p-4 border-l-4 border-l-amber-500"><div className="overline">Outstanding</div><div className="mt-1 font-display text-2xl font-bold tabular-nums text-amber-700">{formatCurrency(totalDue)}</div></div>
        <div className="dc-card p-4 border-l-4 border-l-red-500"><div className="overline">Overdue</div><div className="mt-1 font-display text-2xl font-bold tabular-nums text-red-700">{formatCurrency(overdue)}</div></div>
      </div>

      <div className="dc-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="font-display text-[15px] font-semibold text-slate-900">Tax liabilities</h3>
          {canEdit && <Button size="sm" onClick={() => setOpen(true)} data-testid="tax-add-btn"><Plus size={12} className="mr-1" /> Add</Button>}
        </div>
        {items.length === 0 ? <EmptyState icon={CurrencyDollar} title="No tax records yet" />
         : (
          <div className="responsive-table-wrap">
            <table className="data-table w-full min-w-[900px]">
              <thead><tr><th>Period</th><th>Type</th><th className="text-right">Base</th><th className="text-right">Rate</th><th className="text-right">Liability</th><th>Due</th><th>Status</th><th className="text-right">Action</th></tr></thead>
              <tbody>
                {items.map((t) => (
                  <tr key={t.id} data-testid={`tax-row-${t.id}`}>
                    <td className="px-5 py-3 font-mono-alt text-xs">{t.period}</td>
                    <td className="px-5 py-3 text-xs">{(t.tax_type || "").replace(/_/g, " ")}</td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{formatCurrency(t.base_amount)}</td>
                    <td className="px-5 py-3 text-right font-mono-alt text-xs tabular-nums">{t.rate_pct}%</td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums font-semibold">{formatCurrency(t.liability)}</td>
                    <td className="px-5 py-3 text-xs">{t.due_date ? formatDate(t.due_date) : "—"}</td>
                    <td className="px-5 py-3"><StatusBadge status={t.status === "paid" ? "approved" : t.status === "overdue" ? "expired" : "pending"} /></td>
                    <td className="px-5 py-3 text-right">
                      {canEdit && t.status !== "paid" && <Button size="sm" variant="outline" onClick={() => markPaid(t.id)} data-testid={`tax-pay-${t.id}`}><Check size={12} className="mr-1" /> Mark paid</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add tax record</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Period</Label><Input placeholder="2026-Q3 or 2026" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} /></div>
              <div><Label>Type</Label>
                <Select value={form.tax_type} onValueChange={(v) => setForm({ ...form, tax_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="corporate_income">Corporate income</SelectItem>
                    <SelectItem value="withholding">Withholding</SelectItem>
                    <SelectItem value="payroll">Payroll</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Base amount</Label><Input type="number" step="0.01" value={form.base_amount} onChange={(e) => setForm({ ...form, base_amount: parseFloat(e.target.value || 0) })} /></div>
              <div><Label>Rate %</Label><Input type="number" step="0.5" value={form.rate_pct} onChange={(e) => setForm({ ...form, rate_pct: parseFloat(e.target.value || 0) })} /></div>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={submit} className="btn-primary-blue" data-testid="tax-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Section 13.4 — FX Log
// ══════════════════════════════════════════════════════════════
export function FxLogTab({ canEdit }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ rate_date: today, base_currency: "USD", quote_currency: "SOS", rate: 0, source: "Central Bank", notes: "" });

  const load = async () => { const { data } = await api.get("/finance/fx-rates"); setItems(data.items || []); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.quote_currency || !form.rate) return toast.error("Quote currency and rate required");
    try { await api.post("/finance/fx-rates", form); toast.success("FX rate logged"); setOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="dc-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="font-display text-[15px] font-semibold text-slate-900">Currency / FX Log</h3>
          {canEdit && <Button size="sm" onClick={() => setOpen(true)} data-testid="fx-add-btn"><Plus size={12} className="mr-1" /> Log rate</Button>}
        </div>
        {items.length === 0 ? <EmptyState icon={CurrencyDollar} title="No FX rates logged yet" />
         : (
          <div className="responsive-table-wrap">
            <table className="data-table w-full min-w-[700px]">
              <thead><tr><th>Date</th><th>Pair</th><th className="text-right">Rate</th><th>Source</th><th>Notes</th></tr></thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} data-testid={`fx-row-${r.id}`}>
                    <td className="px-5 py-3 font-mono-alt text-xs">{formatDate(r.rate_date)}</td>
                    <td className="px-5 py-3 font-semibold text-slate-900">{r.base_currency} → {r.quote_currency}</td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{r.rate.toLocaleString(undefined, { maximumFractionDigits: 4 })}</td>
                    <td className="px-5 py-3 text-xs text-slate-600">{r.source}</td>
                    <td className="px-5 py-3 text-xs text-slate-500">{r.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Log FX rate</DialogTitle><DialogDescription>USD is always the base currency for accounting.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date</Label><Input type="date" value={form.rate_date} onChange={(e) => setForm({ ...form, rate_date: e.target.value })} /></div>
              <div><Label>Quote currency</Label>
                <Select value={form.quote_currency} onValueChange={(v) => setForm({ ...form, quote_currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SOS">SOS · Somali Shilling</SelectItem>
                    <SelectItem value="KES">KES · Kenyan Shilling</SelectItem>
                    <SelectItem value="EUR">EUR · Euro</SelectItem>
                    <SelectItem value="GBP">GBP · British Pound</SelectItem>
                    <SelectItem value="ETB">ETB · Ethiopian Birr</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Rate (1 USD = ?)</Label><Input type="number" step="0.0001" value={form.rate} onChange={(e) => setForm({ ...form, rate: parseFloat(e.target.value || 0) })} data-testid="fx-rate" /></div>
              <div><Label>Source</Label><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} /></div>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={submit} className="btn-primary-blue" data-testid="fx-save">Log rate</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Section 14 — Finance Overview rewrite (8 KPI cards)
// ══════════════════════════════════════════════════════════════
const TONE_CLS = {
  blue:    "border-l-blue-500 text-blue-700",
  emerald: "border-l-emerald-500 text-emerald-700",
  amber:   "border-l-amber-500 text-amber-700",
  red:     "border-l-red-500 text-red-700",
  slate:   "border-l-slate-400 text-slate-700",
};

export function OverviewC({ onTabChange }) {
  const [data, setData] = useState(null);
  const [payroll, setPayroll] = useState(null);
  useEffect(() => { (async () => {
    try {
      const [{ data: d }, { data: p }] = await Promise.all([
        api.get("/finance/overview-c"),
        api.get("/finance/payroll-status-summary"),
      ]);
      setData(d);
      setPayroll(p);
    } catch (e) { toast.error(formatApiError(e)); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })(); }, []);
  if (!data) return <div className="font-mono-alt text-xs uppercase tracking-[0.25em] text-zinc-500">Loading…</div>;

  const goto = (kpi) => {
    if (kpi.deeplink) { window.location.href = kpi.deeplink; return; }
    if (kpi.tab && onTabChange) onTabChange(kpi.tab);
  };

  const payrollBadgeTone = (status) => {
    if (!status) return "bg-slate-100 text-slate-600";
    if (status === "paid") return "bg-emerald-100 text-emerald-700";
    if (["rejected", "finance_rejected"].includes(status)) return "bg-red-100 text-red-700";
    if (["approved", "finance_cleared"].includes(status)) return "bg-blue-100 text-blue-700";
    return "bg-amber-100 text-amber-700";
  };

  return (
    <div className="space-y-5" data-testid="overview-c">
      {/* Smart Insights — interconnected brain of Finance */}
      <FinanceSmartInsights />

      <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {data.kpis.map((k) => {
          const tone = TONE_CLS[k.tone] || TONE_CLS.slate;
          const display = k.is_count ? String(k.value) : formatCurrency(k.value);
          const isPayrollCard = k.key === "pending_approvals";
          const showPayroll = isPayrollCard && payroll?.latest;
          return (
            <button key={k.key} onClick={() => goto(k)} className={`dc-card p-4 border-l-4 ${tone.split(" ")[0]} text-left transition hover:shadow-md`} data-testid={`overview-kpi-${k.key}`}>
              <div className="overline">{k.label}</div>
              <div className={`mt-1 font-display text-2xl font-bold tabular-nums ${tone.split(" ")[1] || ""}`}>{display}</div>
              {k.secondary && <div className="text-xs text-slate-500 mt-1">Margin {k.secondary}</div>}
              {showPayroll && (
                <div className="mt-2" data-testid="overview-payroll-badge">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono-alt text-[10px] uppercase tracking-widest ${payrollBadgeTone(payroll.latest.status)}`}>
                    Payroll · {payroll.latest.stage_label}
                  </span>
                  {payroll.total_pending > 0 && (
                    <div className="mt-1 text-[10px] text-slate-500">
                      {payroll.total_pending} payroll request{payroll.total_pending === 1 ? "" : "s"} in flight
                      {payroll.latest.period_month ? ` · ${payroll.latest.period_month}` : ""}
                    </div>
                  )}
                </div>
              )}
              {k.tab && <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono-alt uppercase tracking-widest text-slate-400">View <ArrowRight size={10} /></div>}
            </button>
          );
        })}
      </section>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="dc-card overflow-hidden">
          <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-2">
            <Receipt size={18} weight="fill" style={{ color: "#1E5CBA" }} />
            <h3 className="font-display text-[15px] font-semibold text-slate-900">Recent transactions</h3>
            <span className="ml-auto font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">last 8</span>
          </div>
          {data.recent_transactions.length === 0 ? <EmptyState icon={Coins} title="No transactions yet" />
           : (
            <ul className="divide-y divide-slate-100" data-testid="overview-recent-tx">
              {data.recent_transactions.map((tx, i) => (
                <li key={`${tx.date}-${tx.category}-${tx.amount}-${i}`} className="flex items-center gap-3 px-5 py-3">
                  <span className="font-mono-alt w-24 text-xs text-slate-500">{formatDate(tx.date)}</span>
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-900">{tx.category}{tx.vendor_or_client ? ` · ${tx.vendor_or_client}` : ""}</div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">{(tx.type || "").replace("_", " ")}</div>
                  </div>
                  <span className={`font-mono-alt tabular-nums font-semibold ${tx.type === "revenue" ? "text-emerald-700" : "text-slate-900"}`}>{formatCurrency(tx.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="dc-card overflow-hidden">
          <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-2">
            <FileText size={18} weight="fill" className="text-amber-600" />
            <h3 className="font-display text-[15px] font-semibold text-slate-900">Upcoming obligations</h3>
            <span className="ml-auto font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">next 30 days</span>
          </div>
          {data.upcoming_obligations.length === 0 ? <EmptyState icon={Coins} title="Nothing due in the next 30 days" />
           : (
            <ul className="divide-y divide-slate-100" data-testid="overview-upcoming">
              {data.upcoming_obligations.map((u, i) => (
                <li key={i} className="flex items-center gap-3 px-5 py-3">
                  <span className={`font-mono-alt rounded px-1.5 py-0.5 text-[10px] uppercase tracking-widest ${u.kind === "AP" ? "bg-red-100 text-red-700" : u.kind === "AR" ? "bg-emerald-100 text-emerald-700" : u.kind === "TAX" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>{u.kind}</span>
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-900">{u.label}</div>
                    <div className="text-xs text-slate-500">due {formatDate(u.due_date)}</div>
                  </div>
                  {u.amount != null && <span className="font-mono-alt tabular-nums text-sm">{formatCurrency(u.amount)}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
